import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Spin } from "antd";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import {
  gender,
  REGISTRATION_SORT_OPTIONS,
  status,
} from "../../../../core/common/selectoption/selectoption";
import TooltipOption from "../../../../core/common/tooltipOption";
import { getAllRegistration } from "../../../../service/registration";
import { all_routes } from "../../../router/all_routes";
import RegistrationModals from "../registrationModals";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";
import { Class, getAllClasses } from "../../../../service/classes";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { TableData } from "../../../../core/data/interface";
import { getAllGroups } from "../../../../service/test-group";
import { formatDate } from "../../../../utils/dateutils";
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos";
import { useSelector } from "react-redux";

type paginationDataType = {
  currentPage: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  limit: number;
  total?: number;
  totalPages?: number;
};

interface RegistrationData extends TableData {
  registrationNumber: string;
  firstName: string;
  gender: string;
  status: string;
  createdAt: string;
  dateOfBirth: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  _id: string;
  academicYear: string;
  appliedClass: { _id: string; name: string; registrationType: string };
  studentPicture: string;
  lastName: string;
  fatherName: string;
  interviewTime: string;
  interviewDate: string;
}

const RegistrationGrid = () => {
  const componentRef = useRef<HTMLDivElement>(null);
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RegistrationData[]>([]);
  const [paginationData, setPaginationData] = useState<paginationDataType>({
    currentPage: 1,
    limit: 10,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [classes, setAllClasses] = useState<Option[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [filters, setFilters] = useState<{
    class?: Option;
    gender?: Option;
    status?: Option;
  } | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    gender?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    system?: string;
  } | null>({ system: systems[0]?._id });

  const [sortBy, setSortBy] = useState(REGISTRATION_SORT_OPTIONS[0]);

  const [applyLoading, setApplyLoading] = useState(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegistrationData | null>(null);
  const routes = all_routes;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const skipFetchApi = useRef(true);

  const fetchTestGroupsForRegistration = async (registrationId: string) => {
    try {
      // Find the registration from existing data
      const registration = data.find((reg) => reg._id === registrationId);

      if (!registration) {
        console.error("Registration not found");
        return null;
      }

      // Fetch test groups for this registration's class
      const response = await getAllGroups({
        class: registration.appliedClass._id, // Use class ID from registration
        limit: 1,
      });

      return response.data[0] || null;
    } catch (error) {
      console.error("Error fetching test groups:", error);
      return null;
    }
  };

  const handleAdmitCardClick = (registration: any) => {
    setSelectedRegistration(registration);
  };

  const handleApplyClick = async () => {
    setApplyLoading(true);
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    try {
      setAppliedFilters({
        class: filters?.class?.value as string,
        gender: filters?.gender?.value as string,
        status: filters?.status?.value as string,
        system: appliedFilters?.system,
      });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loading) return;
    setPaginationData((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  };

  const fetchRegistrations = async () => {
    if (skipFetchApi.current) {
      return;
    }
    try {
      setLoading(true);
      const response = await getAllRegistration({
        page: paginationData.currentPage,
        limit: paginationData.limit,
        ...appliedFilters,
        ...sortBy.value,
      });
      setData((prev) => [...prev, ...response.registrations]);
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const refetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await getAllRegistration({
        page: 1,
        limit: paginationData.limit,
        ...appliedFilters,
        ...sortBy.value,
      });
      setData(response.registrations);
      setPaginationData({
        ...response.pagination,
        currentPage: 1,
      });
      skipFetchApi.current = false;
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    refetchRegistrations();
  }, [appliedFilters, sortBy]);

  useEffect(() => {
    fetchRegistrations();
  }, [paginationData.currentPage]);

  const fetchClasses = async () => {
    setClassesLoading(true);
    try {
      const response = await getAllClasses();
      const formattedClasses = response.classes.map((classItem: Class) => ({
        label: classItem.name,
        value: classItem._id,
      }));
      setAllClasses(formattedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setClassesLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      class: {
        label: "Select",
        value: "",
      },
      gender: {
        label: "Select",
        value: "",
      },
      status: {
        label: "Select",
        value: "",
      },
    });
    setAppliedFilters({ system: systems[0]?._id });
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        "Registration Number": item.registrationNumber,
        "Student Name": `${item.firstName} ${item.lastName}`,
        "Apllied Class": item.appliedClass.name,
        Gender: item.gender,
        Status: item.status,
        "Applied On": formatDate(item.createdAt),
        "Father Email": item.fatherEmail,
        "Father Phone": item.fatherPhoneNumber,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registrations");
    XLSX.writeFile(wb, "registrations.xlsx");
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Registrations</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Registrations</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Registration Form Grid
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={refetchRegistrations}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link
                  to={routes.addAdmissionForm}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  New Registration
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 border rounded-1 d-flex align-items-center justify-content-between flex-wrap mb-4 pb-0">
            <h4 className="mb-3">Registration Form Grid</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <CommonSelect
                  className="select"
                  options={systems}
                  value={systems.find(
                    (item) => item._id === appliedFilters?.system
                  )}
                  getOptionValue={(item) => item._id}
                  getOptionLabel={(item) => item.name}
                  onChange={(systemItem) =>
                    setAppliedFilters((prev) => ({
                      ...prev,
                      system: systemItem?._id,
                    }))
                  }
                />
              </div>

              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4>Filter</h4>
                    </div>
                    <div className="p-3 pb-0 border-bottom">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label className="form-label">Class</label>
                            {classesLoading ? (
                              <div> Loading classes... </div>
                            ) : (
                              <CommonSelect
                                className="select"
                                options={classes}
                                value={filters?.class}
                                onChange={(classItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    class: classItem as Option,
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Gender</label>
                            <CommonSelect
                              className="select"
                              options={gender}
                              value={filters?.gender}
                              onChange={(genderItem) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  gender: genderItem as Option,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <CommonSelect
                              className="select"
                              options={status}
                              value={filters?.status}
                              onChange={(statusItem) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  status: statusItem as Option,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 d-flex align-items-center justify-content-end">
                      <div
                        className="btn btn-light me-3"
                        onClick={handleResetFilters}
                      >
                        Reset
                      </div>
                      <div
                        className="btn btn-primary"
                        onClick={handleApplyClick}
                      >
                        {applyLoading ? (
                          <>
                            <i className="ti ti-loader-3 me-2 spin-animation" />
                            Applying...
                          </>
                        ) : (
                          "Apply"
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3 me-2">
                <Link
                  to={routes.registrationFormList}
                  className="btn btn-icon btn-sm me-1 bg-light primary-hover"
                >
                  <i className="ti ti-list-tree" />
                </Link>
                <Link
                  to={routes.registrationFormGrid}
                  className="active btn btn-icon btn-sm primary-hover"
                >
                  <i className="ti ti-grid-dots" />
                </Link>
              </div>
              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by {sortBy.label}
                </Link>
                <ul className="dropdown-menu p-3">
                  {REGISTRATION_SORT_OPTIONS.map((item, index) => (
                    <li key={index}>
                      <div
                        className={`dropdown-item rounded-1 ${
                          item.label === sortBy.label ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSortSelect(item)}
                      >
                        {item.label}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="row printable-content" ref={componentRef}>
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Registration Grid</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            {loading && paginationData.currentPage === 1 ? (
              <Spin
                size="large"
                style={{
                  height: "90vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            ) : data.length > 0 ? (
              data.map((registration) => (
                <div
                  className="col-xxl-3 col-xl-4 col-md-6 d-flex"
                  key={registration._id}
                >
                  <div className="card flex-fill">
                    <div className="card-header d-flex align-items-center justify-content-between">
                      <Link
                        to={routes.admissionFormDetail}
                        className="link-primary"
                        state={registration}
                      >
                        {registration.registrationNumber}
                      </Link>
                      <div className="d-flex align-items-center">
                        <span
                          className={`badge d-inline-flex align-items-center me-1 ${
                            registration.status.toLowerCase() === "accepted"
                              ? "badge-soft-success"
                              : registration.status.toLowerCase() === "rejected"
                              ? "badge-soft-danger"
                              : "badge-soft-warning"
                          }`}
                        >
                          <i className="ti ti-circle-filled fs-5 me-1" />
                          {registration.status}
                        </span>
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="ti ti-dots-vertical fs-14" />
                          </Link>
                          <ul className="dropdown-menu dropdown-menu-right p-3">
                            <li>
                              <Link
                                className="dropdown-item rounded-1"
                                to={routes.admissionFormDetail}
                                state={registration}
                              >
                                <i className="ti ti-menu me-2" />
                                View Form
                              </Link>
                            </li>
                            <li>
                              <Link
                                className="dropdown-item rounded-1"
                                to={routes.editAdmissionForm}
                                state={registration}
                              >
                                <i className="ti ti-edit-circle me-2" />
                                Edit
                              </Link>
                            </li>
                            <li>
                              <Link
                                className="dropdown-item rounded-1"
                                to="#"
                                data-bs-toggle="modal"
                                data-bs-target="#delete-modal"
                                onClick={() => setSelectedId(registration._id)}
                              >
                                <i className="ti ti-trash-x me-2" />
                                Delete
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="bg-light-300 rounded-2 p-3 mb-3">
                        <div className="d-flex align-items-center">
                          <Link
                            to={routes.studentDetail}
                            className="avatar avatar-lg flex-shrink-0"
                          >
                            <ImageWithBasePath
                              src={"assets/img/students/student-01.jpg"}
                              fullSrc={registration.studentPicture}
                              className="img-fluid rounded-circle"
                              alt="img"
                            />
                          </Link>
                          <div className="ms-2">
                            <h5 className="mb-0">
                              <Link to={routes.studentDetail}>
                                {registration.firstName}
                              </Link>
                            </h5>
                            <p>{registration.academicYear}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between gx-2">
                        <div>
                          <p className="mb-0">Applied Class</p>
                          <p className="text-dark">
                            {registration.appliedClass.name}
                          </p>
                        </div>
                        <div>
                          <p className="mb-0">Gender</p>
                          <p className="text-dark">{registration.gender}</p>
                        </div>
                        <div>
                          <p className="mb-0">Applied On</p>
                          <p className="text-dark">
                            {formatDate(registration.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <Link
                          to={`tel:${registration.fatherPhoneNumber}`}
                          className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-2"
                        >
                          <i className="ti ti-phone" />
                        </Link>
                        <Link
                          to={`mailto:${registration.fatherEmail}`}
                          className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-3"
                        >
                          <i className="ti ti-mail" />
                        </Link>
                      </div>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#add_fees_collect"
                        className="btn btn-primary btn-sm fw-semibold"
                        onClick={() => handleAdmitCardClick(registration)}
                      >
                        Admit Card
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center py-5">
                    <div className="empty-state-icon">
                      <i className="ti ti-user-x fs-1 text-muted"></i>
                    </div>
                    <h4 className="text-muted mt-3">No Registrations Found</h4>
                    <p className="text-muted mb-4">
                      {appliedFilters
                        ? "No registrations match the current filter criteria."
                        : "There are no registrations in the system yet."}
                    </p>
                    {appliedFilters && (
                      <button
                        className="btn btn-primary"
                        onClick={handleResetFilters}
                      >
                        <i className="ti ti-filter-off me-2"></i>
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {paginationData?.hasNextPage && (
            <div className="col-md-12 text-center mt-4">
              <button
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="ti ti-loader-3 me-2 spin-animation" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-loader-3 me-2" />
                    Load More
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <RegistrationModals
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        fetchData={refetchRegistrations}
        registrationData={selectedRegistration}
      />
    </div>
  );
};

export default RegistrationGrid;
